// ─── Angular Imports ───────────────────────────────────────────────────────────
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Component, DestroyRef, OnInit, inject, isDevMode, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';

// ─── Third-Party Imports ───────────────────────────────────────────────────────
import { forkJoin, map, switchMap } from 'rxjs';

// ─── Application Imports ───────────────────────────────────────────────────────
import { IOutputsCollection } from 'src/app/models/outputsCollection';
import { AuthService } from 'src/app/services/auth.service';
import { Permission } from 'src/app/constants/permission.constant';
import { OutputsCollectionsService } from 'src/app/services/outputs-collections.service';
import { OutputsCollectionProductListResult } from 'src/app/models/outputs-collections/outputs-collections-result';

@Component({
    selector: 'app-output-collections-latest',
    templateUrl: './output-collections-latest.component.html',
    styleUrls: ['./output-collections-latest.component.scss'],
    changeDetection: ChangeDetectionStrategy.Default,
    standalone: false
})
export class OutputCollectionsLatestComponent implements OnInit {
  // ─── State ───────────────────────────────────────────────────────────────────
  private readonly destroyRef = inject(DestroyRef);
  Permission = Permission;
  latestCollections: IOutputsCollection[] = [];
  colPerScreen = 5;

  // ─── Constructor ─────────────────────────────────────────────────────────────
  constructor(
    private outputsCollectionsService: OutputsCollectionsService,
    private breakpointObserver: BreakpointObserver,
    private router: Router,
    public authService: AuthService,
  ) {}

  // ─── Lifecycle Hooks ────────────────────────────────────────────────────────
  ngOnInit(): void {
    this.breakpointObserver.observe([
      Breakpoints.XLarge,
      Breakpoints.Large,
      Breakpoints.Medium,
      Breakpoints.Small,
      Breakpoints.XSmall,
    ]).pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      this.colPerScreen = this.calculateColPerScreen();
    });

    if (this.authService.hasPermission(Permission.OutputsCollectionsRead)) {
      this.listOutputsCollections();
    }
  }

  // ─── Data Fetching Methods ──────────────────────────────────────────────────
  listOutputsCollections(): void {
    this.outputsCollectionsService.listOutputsCollection().pipe(
      map((collections) =>
        collections
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
          .slice(0, 2)
      ),
      switchMap((collections) =>
        forkJoin(
          collections.map((collection) =>
            this.outputsCollectionsService.getOutputsCollectionProducts(collection.id!).pipe(
              map((result: OutputsCollectionProductListResult) => ({ ...collection, products: result.products }))
            )
          )
        )
      ),
    ).subscribe((collections) => {
      this.latestCollections = collections;
    });
  }

  // ─── Utility Methods ────────────────────────────────────────────────────────
  calculateColPerScreen(): number {
    switch (true) {
      case this.breakpointObserver.isMatched(Breakpoints.XLarge):
        return 7;
      case this.breakpointObserver.isMatched(Breakpoints.Large):
        return 6;
      case this.breakpointObserver.isMatched(Breakpoints.Medium):
        return 5;
      case this.breakpointObserver.isMatched(Breakpoints.Small):
        return 3;
      case this.breakpointObserver.isMatched(Breakpoints.XSmall):
        return 2;
      default:
        return 1;
    }
  }

  // ─── Action Methods ─────────────────────────────────────────────────────────
  manageCollection(id: string): void {
    this.router.navigateByUrl(`/collections/${id}/products`);
  }

  downloadCollection(downloadUrl: string, id: string): void {
    this.outputsCollectionsService.downloadOutputsCollection(id).subscribe(() => {
      if (isDevMode()) {
        console.log('[DEV] [OutputCollectionsLatestComponent] downloading collection', id);
      }
    });
    window.open(downloadUrl, '_blank');
  }
}
